// Accès à l'API de synocore, qui détient le journal unifié `BatchRun`.
//
// Synobatch n'a PAS de backend : c'est un front statique servi par nginx qui
// appelle directement synocore en cross-origin. L'authentification repose
// entièrement sur le cookie SSO `syn_session`, posé par synocore sur
// `Domain=.synotis.tech` — d'où `credentials: 'include'` sur chaque appel.
//
// Deux conséquences à connaître :
//   1. l'origine de synobatch DOIT figurer dans `ALLOWED_ORIGINS` de synocore,
//      sinon le navigateur bloque la réponse (CORS avec credentials interdit
//      le joker `*`) ;
//   2. en développement (`localhost:5176`), le cookie de production n'est pas
//      envoyé : on travaille contre le core local, ou contre staging après
//      s'être authentifié sur `auth-staging.synotis.tech`.

/**
 * URL de l'API core, déduite du hostname — même principe que `EnvBanner` :
 * détection 100 % runtime, aucun rebuild ni variable d'environnement par cible.
 * Un seul artefact `dist/` peut donc être déployé sur n'importe quel environnement.
 */
export function coreBaseUrl() {
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) {
    return 'http://localhost:4100';
  }
  if (host.includes('staging')) {
    return 'https://auth-staging.synotis.tech';
  }
  return 'https://auth.synotis.tech';
}

/**
 * URL de connexion SSO — le login est délégué à synocore, jamais réimplémenté.
 *
 * `origin` est validé côté core contre `ALLOWED_ORIGINS` (protection
 * anti-open-redirect) : l'origine de synobatch doit donc y figurer, sans quoi
 * le retour après authentification retomberait sur une autre application.
 *
 * @param next chemin de retour dans synobatch après authentification.
 */
export function loginUrl(next = '/') {
  const url = new URL('/auth/google', coreBaseUrl());
  url.searchParams.set('origin', window.location.origin);
  url.searchParams.set('next', next);
  return url.toString();
}

/**
 * URL de l'API d'une application MÉTIER de l'écosystème (synolog, synosight).
 *
 * La console lit le journal dans synocore, mais AGIT sur un batch en
 * s'adressant à l'application qui l'exécute : synocore n'a aucun chemin
 * d'exécution vers elles, et lui en donner ferait de l'IdP un ordonnanceur.
 *
 * C'est possible sans rien inventer : le cookie SSO est posé sur
 * `Domain=.synotis.tech`, donc `synobatch.synotis.tech` et
 * `synolog.synotis.tech` sont **same-site**. L'appel est cross-origin (l'origine
 * de synobatch doit figurer dans le `ALLOWED_ORIGINS` de l'app cible) mais pas
 * cross-site : le cookie part, `SameSite=Lax` est respecté.
 *
 * Même détection par hostname que `coreBaseUrl` : un seul artefact `dist/`.
 */
export function appBaseUrl(app) {
  const host = window.location.hostname;
  const local = { synolog: 'http://localhost:4000', synosight: 'http://localhost:3002' };
  if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) {
    return local[app] ?? null;
  }
  const suffix = host.includes('staging') ? '-staging' : '';
  if (app === 'synolog' || app === 'synosight') {
    return `https://${app}${suffix}.synotis.tech`;
  }
  // synocore ou application inconnue : rien à piloter en direct.
  return null;
}

/**
 * Nom du cookie CSRF, qui DÉPEND de l'environnement.
 *
 * Staging et production partagent le domaine `.synotis.tech` : leurs cookies
 * porteraient le même nom et s'écraseraient l'un l'autre. D'où le suffixe
 * `_stg` côté staging (`SESSION_COOKIE_NAME` / `CSRF_COOKIE_NAME`), et donc
 * DEUX jeux de cookies coexistant dans le navigateur de quelqu'un qui utilise
 * les deux environnements.
 *
 * Conséquence directe, et piège vécu : lire « le » cookie `syn_csrf` depuis
 * staging renvoie le jeton de PRODUCTION, que staging rejette en « Jeton CSRF
 * invalide ». Aucun repli d'un nom sur l'autre ici : envoyer le jeton d'un autre
 * environnement est exactement le bug qu'on corrige.
 */
function csrfCookieName() {
  return window.location.hostname.includes('staging') ? 'syn_csrf_stg' : 'syn_csrf';
}

/**
 * Jeton CSRF de la session, en clair dans le cookie CSRF de l'environnement.
 *
 * Le core pose DEUX cookies au login : `syn_session` (HttpOnly, la preuve
 * d'identité) et `syn_csrf` (lisible en JS, sur le même `Domain=.synotis.tech`).
 * Les applications qui protègent leurs mutations en double-submit — synolog le
 * fait sur TOUTES ses mutations par cookie — exigent que ce jeton soit recopié
 * dans l'en-tête `X-CSRF-Token`.
 *
 * Le principe : un site tiers peut faire partir une requête avec nos cookies,
 * mais ne peut pas LIRE le cookie pour en recopier la valeur dans un en-tête.
 * D'où l'importance de ne l'envoyer que sur les mutations, et jamais ailleurs.
 */
function csrfToken() {
  const name = csrfCookieName().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/** Erreur d'API portant le statut HTTP, pour que l'UI distingue 401 / 403 / 5xx. */
export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function request(path, { method = 'GET', body, signal, baseUrl } = {}) {
  const base = baseUrl ?? coreBaseUrl();
  const mutating = method !== 'GET' && method !== 'HEAD';
  const token = mutating ? csrfToken() : null;
  const headers = {
    ...(body ? { 'Content-Type': 'application/json' } : {}),
    // Recopié sur les seules mutations : c'est ce qu'attend la protection
    // double-submit, et l'envoyer plus largement ne protégerait rien de plus.
    ...(token ? { 'X-CSRF-Token': token } : {}),
  };
  let res;
  try {
    res = await fetch(`${base}${path}`, {
      method,
      // Le cookie SSO est la seule preuve d'identité : sans lui, tout est 401.
      credentials: 'include',
      headers: Object.keys(headers).length ? headers : undefined,
      body: body ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    // Panne réseau, CORS refusé, core injoignable : le navigateur ne donne
    // aucun détail exploitable, on rend un message actionnable.
    throw new ApiError(
      `API injoignable (${base}). Vérifier qu'elle répond et que cette origine est autorisée (ALLOWED_ORIGINS).`,
      0,
    );
  }

  if (res.status === 204) return null;

  const payload = await res.json().catch(() => null);
  if (!res.ok) {
    // Le jeton CSRF est posé au LOGIN : une session ouverte avant que
    // l'application ne l'exige n'en a pas, et aucun réessai n'y changera rien.
    // Le message doit donc dire quoi faire, pas seulement ce qui a échoué.
    if (res.status === 403 && /csrf/i.test(payload?.error ?? '')) {
      throw new ApiError(
        csrfToken()
          ? `Jeton CSRF refusé par ${base}. Se déconnecter puis se reconnecter sur cet environnement.`
          : `Aucun cookie « ${csrfCookieName()} » : se connecter sur cet environnement pour en obtenir un.`,
        res.status,
      );
    }
    throw new ApiError(payload?.error || `Erreur ${res.status}`, res.status);
  }
  return payload;
}

/**
 * Utilisateur courant (session SSO), ou `null` si non authentifié.
 *
 * `/internal/me` enveloppe sa réponse dans `{ user: … }` et y ajoute `isAdmin`,
 * dérivé du rôle rechargé en base à chaque appel — une révocation prend donc
 * effet immédiatement, sans attendre l'expiration du cookie.
 */
export async function me(signal) {
  try {
    const payload = await request('/internal/me', { signal });
    return payload?.user ?? payload ?? null;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

/**
 * Liste les exécutions de batch, tous types et toutes applications confondus.
 * C'est l'appel central de la console : le back rend un format COMMUN, la vue
 * n'a donc rien à connaître des batchs individuels.
 *
 * @param {{type?:string, app?:string, status?:string, since?:string, limit?:number, cursor?:string}} filters
 */
export function listRuns(filters = {}, signal) {
  const qs = new URLSearchParams(
    Object.entries(filters).filter(([, v]) => v !== undefined && v !== '' && v !== null),
  );
  const suffix = qs.toString() ? `?${qs}` : '';
  return request(`/api/v1/batch/runs${suffix}`, { signal });
}

/** Détail d'une exécution (rejets ligne à ligne, `meta` spécifique au batch). */
export function getRun(id, signal) {
  return request(`/api/v1/batch/runs/${encodeURIComponent(id)}`, { signal });
}

/**
 * Synthèse par typologie : dernier statut, fraîcheur, taux d'échec. Alimente
 * les tuiles de tête — c'est ce qui donne la « vision par typologie de batch »
 * sans avoir à parcourir l'historique.
 */
export function listTypes(signal) {
  return request('/api/v1/batch/types', { signal });
}

/**
 * Batchs planifiés : expression cron, prochaines occurrences, dernière
 * exécution. Alimente l'onglet « Planification ».
 */
export function listSchedules(signal) {
  return request('/api/v1/batch/schedules', { signal });
}

/** Relance un batch exécuté par synocore (session ADMIN requise). */
export function runBatch(type, options = {}) {
  return request(`/api/v1/batch/${encodeURIComponent(type)}/run`, {
    method: 'POST',
    body: options,
  });
}

/**
 * Modifie les réglages d'un batch exécuté par SYNOCORE : fréquence, pause,
 * mode. Session ADMIN requise.
 *
 * Passer `null` sur un champ rétablit la valeur du code — ce n'est pas
 * équivalent à `false`, qui est une décision explicite.
 *
 * La réponse porte `schedulerActive` et `posedCron` : ce que le moteur a
 * RÉELLEMENT posé, à afficher de préférence à ce qui a été demandé.
 */
export function updateCoreBatchSettings(type, patch) {
  return request(`/api/v1/batch/schedules/${encodeURIComponent(type)}/settings`, {
    method: 'PATCH',
    body: patch,
  });
}

/**
 * Retire une planification ANNONCÉE de l'annuaire (ménage après le retrait
 * d'un batch dans son application). Un batch de synocore n'est pas supprimable :
 * il est déclaré dans le code, et l'API répond 409 en invitant à le mettre en
 * pause.
 */
export function deleteSchedule(type) {
  return request(`/api/v1/batch/schedules/${encodeURIComponent(type)}`, { method: 'DELETE' });
}

/**
 * Relance un batch exécuté par une application tierce, en s'adressant à elle.
 *
 * Réponse 202 : une synchronisation dure plusieurs minutes, l'application ne
 * l'attend pas. Le résultat se lit dans le journal, comme tout le reste.
 */
export function runAppBatch(app, type, options = {}) {
  const baseUrl = appBaseUrl(app);
  if (!baseUrl) throw new ApiError(`Application « ${app} » non pilotable depuis cette console.`, 0);
  return request(`/api/batch/${encodeURIComponent(type)}/run`, {
    method: 'POST',
    body: options,
    baseUrl,
  });
}

/**
 * Change le mode (simulation ↔ écriture) ou l'armement d'un batch tiers.
 * Le réglage vit en base côté application : effet immédiat, sans déploiement.
 */
export function updateAppBatchSettings(app, type, patch) {
  const baseUrl = appBaseUrl(app);
  if (!baseUrl) throw new ApiError(`Application « ${app} » non pilotable depuis cette console.`, 0);
  return request(`/api/batch/${encodeURIComponent(type)}/settings`, {
    method: 'PATCH',
    body: patch,
    baseUrl,
  });
}
